import { useEffect, useState } from "react";
import { BellRing, BellOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;

const toBytes = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  return Uint8Array.from(atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
};

const currentSub = async () => {
  const reg = await navigator.serviceWorker.ready;
  return { reg, sub: await reg.pushManager.getSubscription() };
};

export const pushSupported = supported;
export const needsIosInstall = () => isIos() && !standalone();

// Keeps alerts on: whenever permission is granted, (re)create the subscription and link it to this member.
export const ensurePush = async () => {
  if (!supported() || Notification.permission !== "granted") return false;
  const { data } = await api.get("/push/public-key");
  const { reg, sub: existing } = await currentSub();
  const sub = existing || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toBytes(data.key) });
  await api.post("/push/subscribe", sub.toJSON());
  return true;
};
export const syncPushSubscription = ensurePush;

export const PushToggle = () => {
  const { t } = useTranslation();
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!supported()) { setState(isIos() && !standalone() ? "ios" : "unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    currentSub().then(({ sub }) => setState(sub && Notification.permission === "granted" ? "on" : "off")).catch(() => setState("off"));
  }, []);

  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "denied" : "off"); return; }
      await ensurePush();
      setState("on");
      toast.success(t("inbox.pushEnabled"));
    } catch { toast.error(t("inbox.pushUnsupported")); }
  };

  const disable = async () => {
    const { sub } = await currentSub();
    if (sub) { await api.post("/push/unsubscribe", sub.toJSON()).catch(() => {}); await sub.unsubscribe(); }
    setState("off");
  };

  if (state === "loading") return null;
  const box = "px-4 py-3 border-b border-white/10 text-xs";
  if (state === "ios") return <p data-testid="push-ios-hint" className={`${box} text-zinc-400`}>{t("inbox.pushIos")}</p>;
  if (state === "unsupported") return <p data-testid="push-unsupported" className={`${box} text-zinc-500`}>{t("inbox.pushUnsupported")}</p>;
  if (state === "denied") return <p data-testid="push-denied" className={`${box} text-amber-300`}>{t("inbox.pushDenied")}</p>;
  if (state === "on") return (
    <div data-testid="push-on" className={`${box} flex items-center justify-between text-emerald-300`}>
      <span className="flex items-center gap-1.5"><BellRing size={14} /> {t("inbox.pushOn")}</span>
      <button data-testid="push-disable-btn" onClick={disable} className="text-zinc-400 hover:text-white underline">{t("inbox.pushOff")}</button>
    </div>
  );
  return (
    <div className={box}>
      <button data-testid="push-enable-btn" onClick={enable}
        className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-red-600 text-white font-bold text-sm py-2 rounded-lg transition-colors">
        <BellOff size={15} /> {t("inbox.pushEnable")}
      </button>
    </div>
  );
};
